/**
 * useRoom Hook
 *
 * Manages room state, messages, and participant data using Yjs CRDT.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { Libp2p } from 'libp2p'
import type {
  RoomMeta,
  Participant,
  QuickActionKey,
  GeoLocation,
  Role,
} from '@/lib/types'
import {
  getRoomDocument,
  destroyRoomDocument,
  getRoomMeta,
  updateRoomMeta,
  initializeRoomMeta,
  addParticipant,
  removeParticipant,
  updateParticipant,
  getParticipants,
  addMessage as addCRDTMessage,
  getMessages as getCRDTMessages,
  addPOI,
  getActivePOIs,
  clearPOI as clearCRDTPOI,
  joinSpeakingQueue,
  leaveSpeakingQueue,
  getSpeakingQueue,
  onDocumentUpdate,
  applyStateUpdate,
  setRoleAssignment,
  getRoleAssignment,
  setOrganizerPublicKey,
  getOrganizerPublicKey,
  type RoomDocument,
  type MessageEntry,
  type POIEntry,
} from '@/lib/sync'
import {
  subscribeToRoomState,
  publishRoomState,
  subscribeToRoomSync,
  publishRoomSync,
  subscribeToGlobalPresence,
  publishGlobalPresence,
  BOOTSTRAP_NODES,
} from '@/lib/p2p'
import { encodeState } from '@/lib/sync'
import {
  deriveRoomKey,
  encryptForRoom,
  decryptFromRoom,
  signMessageEntry,
  verifyMessageEntry,
  verifySenderIdentity,
  getNextSequence,
  checkAndRecordSequence,
  clearRoomSequences,
  initializeFromMessages,
  isMessageFresh,
  createRoleAssignment,
  isOrganizerByKey,
} from '@/lib/crypto'
import type { RoomIdentity } from './useIdentity'

export interface UseRoomOptions {
  roomId: string
  roomSecret: string
  /** Optional PIN for cryptographic room protection */
  roomPin?: string
  node: Libp2p | null
  /** Full identity with keypairs for message signing */
  identity: RoomIdentity | null
  /** Initial room metadata - organizer provides this to initialize the CRDT */
  initialRoomMeta?: RoomMeta
}

export interface UseRoomReturn {
  isConnected: boolean
  isEnded: boolean
  roomMeta: RoomMeta | null
  participants: Participant[]
  messages: MessageEntry[]
  activePOIs: POIEntry[]
  speakingQueue: string[]
  currentSpeaker: string | null
  roomDoc: RoomDocument | null // Exposed for Bluetooth bridge

  // Actions (async for message signing)
  sendTextMessage: (content: string) => Promise<void>
  sendVoiceMessage: (
    audioBlob: Uint8Array,
    duration: number,
    transcript?: string,
    confidence?: number
  ) => Promise<void>
  sendPOI: (type: QuickActionKey, location: GeoLocation, address?: string) => Promise<void>
  sendVehicleSighting: (sighting: {
    location: GeoLocation
    plate?: string
    plateConfidence?: 'full' | 'partial' | 'none'
    vehicleType?: 'sedan' | 'suv' | 'van' | 'truck' | 'unmarked' | 'other'
    vehicleColor?: string
    direction?: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'stationary'
    description?: string
    isFollowing?: boolean
    photos?: Array<{
      id: string
      thumbnailDataUrl: string
      hasLocation: boolean
      plateOCR?: { text: string; confidence: number }
    }>
  }) => Promise<void>
  clearPOI: (poiId: string) => void
  joinQueue: () => void
  leaveQueue: () => void
  updateMeta: (updates: Partial<RoomMeta>) => void
  updateParticipantRole: (peerId: string, role: Role) => Promise<void>
  updateMyGeofenceStatus: (isInside: boolean) => void
  kickParticipant: (peerId: string) => void
  endRoom: () => void
  leave: () => void
}

export function useRoom(options: UseRoomOptions): UseRoomReturn {
  const { roomId, roomSecret, roomPin, node, identity, initialRoomMeta } = options

  // Extract identity fields (with fallbacks for initial render)
  const myPeerId = identity?.peerId ?? ''
  const myDisplayName = identity?.displayName ?? ''

  const [roomDoc, setRoomDoc] = useState<RoomDocument | null>(null)
  const [roomKey, setRoomKey] = useState<CryptoKey | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isEnded, setIsEnded] = useState(false)
  const [roomMeta, setRoomMeta] = useState<RoomMeta | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [messages, setMessages] = useState<MessageEntry[]>([])
  const [activePOIs, setActivePOIs] = useState<POIEntry[]>([])
  const [speakingQueue, setSpeakingQueue] = useState<string[]>([])

  // Refs to hold current values without triggering re-subscriptions
  const roomDocRef = useRef<RoomDocument | null>(null)
  const roomKeyRef = useRef<CryptoKey | null>(null)
  const isSubscribedRef = useRef(false)

  // Initialize room document and encryption key
  useEffect(() => {
    if (!roomId || !roomSecret || !identity) return

    let currentDoc: RoomDocument | null = null

    const initRoom = async () => {
      // Reset join time for fresh sync window
      joinedAtRef.current = Date.now()

      console.log('[useRoom] Initializing room:', {
        roomId,
        secretPrefix: roomSecret?.substring(0, 8),
        hasPin: !!roomPin,
        hasIdentity: !!identity,
        identityPeerId: identity?.peerId?.substring(0, 12),
        role: identity?.role,
      })

      // Get or create room document
      const doc = getRoomDocument(roomId)
      currentDoc = doc
      setRoomDoc(doc)

      // Derive encryption key (includes PIN if set for cryptographic enforcement)
      const key = await deriveRoomKey(roomSecret, roomId, roomPin)
      setRoomKey(key)
      console.log('[useRoom] Derived room encryption key')

      // Initialize room metadata if organizer and not already set
      // This ensures the room metadata is available to all participants via CRDT sync
      const existingMeta = getRoomMeta(doc)
      if (!existingMeta && initialRoomMeta) {
        console.log('[useRoom] Initializing room metadata as organizer')
        initializeRoomMeta(doc, initialRoomMeta)

        // Set the organizer's public key for cryptographic role verification
        setOrganizerPublicKey(doc, identity.publicKeyBase64)
        console.log('[useRoom] Set organizer public key:', identity.publicKeyBase64.substring(0, 20) + '...')
      }

      // Load initial state
      setRoomMeta(getRoomMeta(doc))
      setParticipants(getParticipants(doc))
      setMessages(getCRDTMessages(doc, 100))
      setActivePOIs(getActivePOIs(doc))
      setSpeakingQueue(getSpeakingQueue(doc))

      // Add ourselves as participant
      addParticipant(doc, {
        peerId: identity.peerId,
        displayName: identity.displayName,
        role: identity.role,
        joinedAt: new Date(),
        isInsideGeofence: true,
        lastSeen: new Date(),
        isMuted: false,
      })

      setIsConnected(true)
    }

    initRoom()

    return () => {
      // Cleanup on unmount using local variable
      if (currentDoc && identity) {
        removeParticipant(currentDoc, identity.peerId)
      }
    }
  }, [roomId, roomSecret, roomPin, identity, initialRoomMeta])

  // Keep refs in sync with state (without triggering effects)
  useEffect(() => {
    roomDocRef.current = roomDoc
  }, [roomDoc])

  useEffect(() => {
    roomKeyRef.current = roomKey
  }, [roomKey])

  // Cache for verified message IDs to avoid re-verification
  const verifiedMessageIdsRef = useRef<Set<string>>(new Set())
  // Track when we joined to allow syncing older messages during initial load
  const joinedAtRef = useRef<number>(Date.now())

  // Subscribe to CRDT updates
  useEffect(() => {
    if (!roomDoc) return

    console.log('[useRoom] Setting up CRDT update subscription')

    // Initialize sequence tracking from existing messages
    const existingMessages = getCRDTMessages(roomDoc, 100)
    initializeFromMessages(roomId, existingMessages)

    // Pre-populate verified cache with existing messages (they were already in the CRDT)
    for (const msg of existingMessages) {
      verifiedMessageIdsRef.current.add(msg.id)
    }

    const unsubscribe = onDocumentUpdate(roomDoc, async () => {
      setRoomMeta(getRoomMeta(roomDoc))
      setParticipants(getParticipants(roomDoc))
      setActivePOIs(getActivePOIs(roomDoc))
      setSpeakingQueue(getSpeakingQueue(roomDoc))

      // Get messages and verify signatures + replay protection
      const allMessages = getCRDTMessages(roomDoc, 100)
      const verifiedCache = verifiedMessageIdsRef.current

      // Verify only NEW messages (not already in cache)
      const verifiedMessages = await Promise.all(
        allMessages.map(async (msg) => {
          // System messages bypass verification
          if (msg.type === 'system') {
            verifiedCache.add(msg.id)
            return { msg, isValid: true }
          }

          // Skip re-verification for already verified messages
          if (verifiedCache.has(msg.id)) {
            return { msg, isValid: true }
          }

          // Check signature
          const signatureValid = await verifyMessageEntry(msg)
          if (!signatureValid) {
            console.warn('[useRoom] Message failed signature verification:', msg.id, msg.senderId)
            return { msg, isValid: false }
          }

          // Verify sender identity (senderId derived from public key)
          const senderValid = await verifySenderIdentity(msg)
          if (!senderValid) {
            console.warn('[useRoom] Message failed sender identity verification:', msg.id, msg.senderId)
            return { msg, isValid: false }
          }

          // Check message freshness - but allow older messages during initial sync
          // For the first 10 seconds (IndexedDB sync), skip freshness check entirely
          // For up to 60 seconds, allow messages up to 24 hours old (room session history)
          // After that, only accept messages up to 5 minutes old (real-time protection)
          const timeSinceJoin = Date.now() - joinedAtRef.current
          const isIndexDbSync = timeSinceJoin < 10000 // First 10 seconds
          const isInitialSync = timeSinceJoin < 60000 // First 60 seconds

          // During IndexedDB sync, trust all local messages
          if (!isIndexDbSync) {
            const maxAge = isInitialSync ? 24 * 60 * 60 * 1000 : 5 * 60 * 1000 // 24 hours vs 5 min

            if (!isMessageFresh(msg.timestamp, maxAge)) {
              console.warn('[useRoom] Stale message detected:', msg.id, msg.timestamp, isInitialSync ? '(initial sync)' : '')
              return { msg, isValid: false }
            }
          }

          // Check and record sequence number for replay protection
          if (msg.sequence !== undefined) {
            const sequenceValid = checkAndRecordSequence(roomId, msg.senderId, msg.sequence)
            if (!sequenceValid) {
              console.warn('[useRoom] Replay detected:', msg.id, msg.senderId, msg.sequence)
              return { msg, isValid: false }
            }
          }

          // Message passed all checks - add to verified cache
          verifiedCache.add(msg.id)
          return { msg, isValid: true }
        })
      )

      // Only show messages that passed all verification
      setMessages(verifiedMessages.filter(({ isValid }) => isValid).map(({ msg }) => msg))
    })

    // Capture ref value for cleanup
    const verifiedMessageIds = verifiedMessageIdsRef.current

    return () => {
      unsubscribe()
      clearRoomSequences(roomId)
      verifiedMessageIds.clear()
    }
  }, [roomDoc, roomId])

  // Subscribe to P2P topics - use refs to avoid re-subscribing on every render
  useEffect(() => {
    if (!node || !roomId) return

    // Wait for room doc and key to be ready before subscribing
    const waitAndSubscribe = async () => {
      // Wait for initialization (with timeout)
      let attempts = 0
      while ((!roomDocRef.current || !roomKeyRef.current) && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100))
        attempts++
      }

      if (!roomDocRef.current || !roomKeyRef.current) {
        console.error('[useRoom] Timeout waiting for room initialization')
        return
      }

      // Prevent double subscription
      if (isSubscribedRef.current) {
        return
      }
      isSubscribedRef.current = true
      console.log('[useRoom] Setting up P2P subscriptions for room:', roomId)
      console.log('[useRoom] P2P node peer ID:', node?.peerId?.toString()?.substring(0, 20) + '...')
      console.log('[useRoom] GossipSub topic:', `/crowdtalkie/room/${roomId}/state`)

      // Subscribe to state updates - use refs inside handler
      const unsubState = subscribeToRoomState(node, roomId, async (data) => {
        try {
          const key = roomKeyRef.current
          const doc = roomDocRef.current
          if (!key || !doc) return

          const decrypted = await decryptFromRoom(data, key)
          applyStateUpdate(doc, decrypted)
        } catch (err) {
          console.error('[useRoom] Failed to apply state update:', err)
        }
      })

      // Broadcast our state updates (local only - don't re-broadcast remote updates)
      const doc = roomDocRef.current
      const unsubDoc = onDocumentUpdate(doc, async (update) => {
        try {
          const key = roomKeyRef.current
          if (!key) return

          const encrypted = await encryptForRoom(update, key)
          await publishRoomState(node, roomId, encrypted)
        } catch (err) {
          console.error('[useRoom] Failed to publish state:', err)
        }
      }, true) // localOnly = true to prevent echo loops

      // Track peers we've already tried to connect to (avoid repeated dials)
      const dialedPeers = new Set<string>()

      // Use the first bootstrap node as the relay for circuit relay connections
      const RELAY_ADDR = BOOTSTRAP_NODES[0]

      // Function to dial a peer through circuit relay
      // Returns true if connection was established (or already exists), false if dial failed
      const dialPeerViaRelay = async (remotePeerId: string): Promise<boolean> => {
        if (dialedPeers.has(remotePeerId)) return true // Already processed
        if (remotePeerId === node.peerId.toString()) return false // Don't dial ourselves

        dialedPeers.add(remotePeerId)

        try {
          // Check if already connected
          const connections = node.getConnections()
          const alreadyConnected = connections.some(conn => conn.remotePeer.toString() === remotePeerId)
          if (alreadyConnected) {
            console.log('[useRoom] Already connected to peer:', remotePeerId.substring(0, 12))
            return true
          }

          // Dial via circuit relay: relay-addr/p2p-circuit/p2p/remote-peer-id
          const circuitAddr = `${RELAY_ADDR}/p2p-circuit/p2p/${remotePeerId}`
          console.log('[useRoom] Dialing peer via circuit relay:', remotePeerId.substring(0, 12))

          const { multiaddr } = await import('@multiformats/multiaddr')
          const ma = multiaddr(circuitAddr)
          await node.dial(ma, { signal: AbortSignal.timeout(15000) })
          console.log('[useRoom] Successfully connected to peer via relay:', remotePeerId.substring(0, 12))
          return true
        } catch (err) {
          console.warn('[useRoom] Failed to dial peer via relay:', remotePeerId.substring(0, 12), err)
          // Remove from dialed set so we can retry later
          dialedPeers.delete(remotePeerId)
          return false
        }
      }

      // Subscribe to GLOBAL presence topic (unencrypted - relay forwards to all clients)
      // This is the key mechanism for peer discovery - the relay subscribes to this topic
      // and forwards presence announcements to all connected clients
      //
      // PRIVACY NOTE: Room IDs and peer IDs are sent in plaintext on the global presence topic.
      // This is a deliberate tradeoff for peer discovery - the relay needs to forward these
      // messages to enable GossipSub mesh formation. Room contents remain encrypted on
      // room-specific topics. The room ID alone doesn't reveal room name or location.
      const unsubGlobalPresence = subscribeToGlobalPresence(node, async (data: Uint8Array, _fromPeerId: string) => {
        try {
          const messageText = new TextDecoder().decode(data)
          const presenceMsg = JSON.parse(messageText)

          // Only process presence messages for OUR room
          if (presenceMsg.type !== 'presence' || presenceMsg.roomId !== roomId) {
            return
          }

          const incomingPeerId = presenceMsg.peerId
          if (!incomingPeerId || incomingPeerId === node.peerId.toString()) {
            return
          }

          // Use dialedPeers to prevent presence ping-pong storms
          // If we've already processed this peer, don't respond again
          if (dialedPeers.has(incomingPeerId)) {
            console.log('[useRoom] Already processed presence from:', incomingPeerId.substring(0, 12))
            return
          }

          console.log('[useRoom] Received global presence for our room from:', incomingPeerId.substring(0, 12))

          // Dial the peer via circuit relay to establish direct connection
          // Await the dial and only respond with our presence if successful
          // This prevents ping-pong storms when dial fails repeatedly
          const dialSuccess = await dialPeerViaRelay(incomingPeerId)

          if (dialSuccess) {
            // Respond with our own presence so they can connect to us
            const presenceResponse = JSON.stringify({
              type: 'presence',
              roomId: roomId,
              peerId: node.peerId.toString(),
            })
            await publishGlobalPresence(node, new TextEncoder().encode(presenceResponse))
          }
        } catch (err) {
          // Ignore parse errors - might be messages from other apps
          console.debug('[useRoom] Failed to parse global presence:', err)
        }
      })

      // Subscribe to room sync topic for state sync requests/responses (encrypted)
      const unsubSync = subscribeToRoomSync(node, roomId, async (data: Uint8Array, fromPeerId: string) => {
        try {
          const key = roomKeyRef.current
          const doc = roomDocRef.current
          if (!key || !doc) return

          // Decrypt the sync message
          const decrypted = await decryptFromRoom(data, key)
          const messageText = new TextDecoder().decode(decrypted)
          const syncMessage = JSON.parse(messageText)

          if (syncMessage.type === 'request') {
            // Someone is requesting our full state - respond with it
            console.log('[useRoom] Received sync request from:', fromPeerId.substring(0, 12))

            // Encode our full state and send it
            const fullState = encodeState(doc)
            const response = JSON.stringify({
              type: 'response',
              state: btoa(String.fromCharCode(...fullState)),
            })
            const encrypted = await encryptForRoom(new TextEncoder().encode(response), key)
            await publishRoomSync(node, roomId, encrypted)
            console.log('[useRoom] Sent full state sync response')
          } else if (syncMessage.type === 'response') {
            // Received full state from another peer - apply it
            console.log('[useRoom] Received sync response from:', fromPeerId.substring(0, 12))
            const stateBytes = Uint8Array.from(atob(syncMessage.state), c => c.charCodeAt(0))
            applyStateUpdate(doc, stateBytes)
            console.log('[useRoom] Applied full state from peer')
          }
        } catch (err) {
          console.error('[useRoom] Failed to handle sync message:', err)
        }
      })

      // Announce our presence on the GLOBAL presence topic (unencrypted)
      // The relay subscribes to this topic and forwards to all clients
      const announcePresence = async () => {
        try {
          console.log('[useRoom] Announcing presence on global topic...')
          const presence = JSON.stringify({
            type: 'presence',
            roomId: roomId,
            peerId: node.peerId.toString(),
          })
          await publishGlobalPresence(node, new TextEncoder().encode(presence))
        } catch (err) {
          console.error('[useRoom] Failed to announce presence:', err)
        }
      }

      // Request full state sync from other peers - do it multiple times to ensure delivery
      const requestSync = async () => {
        try {
          const key = roomKeyRef.current
          if (!key) return

          console.log('[useRoom] Requesting full state sync from peers...')
          const request = JSON.stringify({ type: 'request', peerId: node.peerId.toString() })
          const encrypted = await encryptForRoom(new TextEncoder().encode(request), key)
          await publishRoomSync(node, roomId, encrypted)
        } catch (err) {
          console.error('[useRoom] Failed to request state sync:', err)
        }
      }

      // Announce presence first, then request sync
      // Presence helps establish connections; sync requests get the room data
      announcePresence() // Immediate presence
      const presenceTimer1 = setTimeout(announcePresence, 2000) // Re-announce after 2s
      const presenceTimer2 = setTimeout(announcePresence, 5000) // Re-announce after 5s
      const presenceTimer3 = setTimeout(announcePresence, 10000) // Re-announce after 10s

      // Request sync after brief delay to allow connections to form
      const syncTimer1 = setTimeout(requestSync, 500) // Quick sync request
      const syncTimer2 = setTimeout(requestSync, 2500) // After first re-announce
      const syncTimer3 = setTimeout(requestSync, 6000) // After second re-announce
      const syncTimer4 = setTimeout(requestSync, 12000) // Final sync attempt

      // Store cleanup functions
      return () => {
        console.log('[useRoom] Cleaning up P2P subscriptions for room:', roomId)
        isSubscribedRef.current = false
        clearTimeout(presenceTimer1)
        clearTimeout(presenceTimer2)
        clearTimeout(presenceTimer3)
        clearTimeout(syncTimer1)
        clearTimeout(syncTimer2)
        clearTimeout(syncTimer3)
        clearTimeout(syncTimer4)
        unsubState()
        unsubDoc()
        unsubSync()
        unsubGlobalPresence()
      }
    }

    let cleanup: (() => void) | undefined
    waitAndSubscribe().then(fn => {
      cleanup = fn
    })

    return () => {
      if (cleanup) cleanup()
      isSubscribedRef.current = false
    }
  }, [node, roomId]) // Only depend on node and roomId - not on objects that might change reference

  // Action handlers
  const sendTextMessage = useCallback(
    async (content: string) => {
      console.log('[useRoom] sendTextMessage called, roomDoc:', !!roomDoc, 'identity:', !!identity, 'content:', content)
      if (!roomDoc || !identity) {
        console.error('[useRoom] sendTextMessage: roomDoc or identity is null!')
        return
      }

      // Get next sequence number for replay protection
      const sequence = getNextSequence(roomId, identity.peerId)

      // Generate message ID upfront so we can add to verified cache
      const messageId = crypto.randomUUID()

      // Create unsigned message with sequence
      const unsignedMessage = {
        id: messageId,
        type: 'text' as const,
        senderId: identity.peerId,
        senderName: identity.displayName,
        senderRole: identity.role,
        timestamp: Date.now(),
        content,
        sequence,
      }

      // Sign the message with our Ed25519 private key
      const signedMessage = await signMessageEntry(
        unsignedMessage,
        identity.privateKey,
        identity.publicKey
      )

      // Pre-add to verified cache BEFORE adding to CRDT
      // This prevents the onDocumentUpdate handler from detecting it as a replay
      verifiedMessageIdsRef.current.add(messageId)

      addCRDTMessage(roomDoc, signedMessage)
    },
    [roomDoc, identity, roomId]
  )

  const sendVoiceMessage = useCallback(
    async (
      audioBlob: Uint8Array,
      duration: number,
      transcript?: string,
      confidence?: number
    ) => {
      if (!roomDoc || !identity) return

      // Convert audio to base64 for CRDT storage
      const audioData = btoa(String.fromCharCode(...audioBlob))

      // Get next sequence number for replay protection
      const sequence = getNextSequence(roomId, identity.peerId)

      // Generate message ID upfront so we can add to verified cache
      const messageId = crypto.randomUUID()

      // Create unsigned message with sequence
      const unsignedMessage = {
        id: messageId,
        type: 'voice' as const,
        senderId: identity.peerId,
        senderName: identity.displayName,
        senderRole: identity.role,
        timestamp: Date.now(),
        audioData,
        duration,
        transcript,
        transcriptConfidence: confidence,
        sequence,
      }

      // Sign the message with our Ed25519 private key
      const signedMessage = await signMessageEntry(
        unsignedMessage,
        identity.privateKey,
        identity.publicKey
      )

      // Pre-add to verified cache BEFORE adding to CRDT
      // This prevents the onDocumentUpdate handler from detecting it as a replay
      verifiedMessageIdsRef.current.add(messageId)

      addCRDTMessage(roomDoc, signedMessage)
    },
    [roomDoc, identity, roomId]
  )

  const sendPOI = useCallback(
    async (type: QuickActionKey, location: GeoLocation, address?: string) => {
      console.log('[useRoom] sendPOI called, roomDoc:', !!roomDoc, 'roomMeta:', !!roomMeta, 'identity:', !!identity, 'type:', type)
      if (!roomDoc || !roomMeta || !identity) {
        console.error('[useRoom] sendPOI: roomDoc, roomMeta, or identity is null!', { roomDoc: !!roomDoc, roomMeta: !!roomMeta, identity: !!identity })
        return
      }

      const poiId = crypto.randomUUID()
      const expiresAt = roomMeta.poiExpirationMinutes > 0
        ? Date.now() + roomMeta.poiExpirationMinutes * 60 * 1000
        : undefined

      addPOI(roomDoc, {
        id: poiId,
        type,
        locationLat: location.latitude,
        locationLng: location.longitude,
        address,
        reporterId: identity.peerId,
        reporterName: identity.displayName,
        createdAt: Date.now(),
        expiresAt,
      })

      // Get next sequence number for replay protection
      const sequence = getNextSequence(roomId, identity.peerId)

      // Generate message ID upfront so we can add to verified cache
      const messageId = crypto.randomUUID()

      // Also add as signed message with sequence
      const unsignedMessage = {
        id: messageId,
        type: 'poi' as const,
        senderId: identity.peerId,
        senderName: identity.displayName,
        senderRole: identity.role,
        timestamp: Date.now(),
        poiType: type,
        locationLat: location.latitude,
        locationLng: location.longitude,
        address,
        expiresAt,
        sequence,
      }

      const signedMessage = await signMessageEntry(
        unsignedMessage,
        identity.privateKey,
        identity.publicKey
      )

      // Pre-add to verified cache BEFORE adding to CRDT
      // This prevents the onDocumentUpdate handler from detecting it as a replay
      verifiedMessageIdsRef.current.add(messageId)

      addCRDTMessage(roomDoc, signedMessage)
    },
    [roomDoc, roomMeta, identity, roomId]
  )

  const sendVehicleSighting = useCallback(
    async (sighting: {
      location: GeoLocation
      plate?: string
      plateConfidence?: 'full' | 'partial' | 'none'
      vehicleType?: 'sedan' | 'suv' | 'van' | 'truck' | 'unmarked' | 'other'
      vehicleColor?: string
      direction?: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'stationary'
      description?: string
      isFollowing?: boolean
      photos?: Array<{
        id: string
        thumbnailDataUrl: string
        hasLocation: boolean
        plateOCR?: { text: string; confidence: number }
      }>
    }) => {
      console.log('[useRoom] sendVehicleSighting called', sighting)
      if (!roomDoc || !roomMeta || !identity) {
        console.error('[useRoom] sendVehicleSighting: roomDoc, roomMeta, or identity is null!')
        return
      }

      const poiId = crypto.randomUUID()
      const expiresAt = roomMeta.poiExpirationMinutes > 0
        ? Date.now() + Math.max(roomMeta.poiExpirationMinutes, 30) * 60 * 1000 // At least 30 min for vehicle sightings
        : Date.now() + 30 * 60 * 1000 // Default 30 min

      // Add to POI map for minimap display
      addPOI(roomDoc, {
        id: poiId,
        type: 'vehicle_sighting',
        locationLat: sighting.location.latitude,
        locationLng: sighting.location.longitude,
        reporterId: identity.peerId,
        reporterName: identity.displayName,
        createdAt: Date.now(),
        expiresAt,
      })

      // Get next sequence number for replay protection
      const sequence = getNextSequence(roomId, identity.peerId)

      // Generate message ID upfront so we can add to verified cache
      const messageId = crypto.randomUUID()

      // Create signed message with vehicle data
      const unsignedMessage = {
        id: messageId,
        type: 'poi' as const,
        senderId: identity.peerId,
        senderName: identity.displayName,
        senderRole: identity.role,
        timestamp: Date.now(),
        poiType: 'vehicle_sighting' as const,
        locationLat: sighting.location.latitude,
        locationLng: sighting.location.longitude,
        expiresAt,
        sequence,
        // Vehicle-specific data
        vehicleData: {
          plate: sighting.plate,
          plateConfidence: sighting.plateConfidence,
          vehicleType: sighting.vehicleType,
          vehicleColor: sighting.vehicleColor,
          direction: sighting.direction,
          description: sighting.description,
          isFollowing: sighting.isFollowing,
          photos: sighting.photos,
        },
      }

      const signedMessage = await signMessageEntry(
        unsignedMessage,
        identity.privateKey,
        identity.publicKey
      )

      // Pre-add to verified cache BEFORE adding to CRDT
      verifiedMessageIdsRef.current.add(messageId)

      addCRDTMessage(roomDoc, signedMessage)
    },
    [roomDoc, roomMeta, identity, roomId]
  )

  const clearPOI = useCallback(
    (poiId: string) => {
      if (!roomDoc) return
      clearCRDTPOI(roomDoc, poiId, myPeerId)
    },
    [roomDoc, myPeerId]
  )

  const joinQueue = useCallback(() => {
    if (!roomDoc) return
    joinSpeakingQueue(roomDoc, myPeerId)
  }, [roomDoc, myPeerId])

  const leaveQueue = useCallback(() => {
    if (!roomDoc) return
    leaveSpeakingQueue(roomDoc, myPeerId)
  }, [roomDoc, myPeerId])

  const updateMeta = useCallback(
    (updates: Partial<RoomMeta>) => {
      if (!roomDoc) return
      updateRoomMeta(roomDoc, updates)
    },
    [roomDoc]
  )

  const updateParticipantRole = useCallback(
    async (peerId: string, role: Role) => {
      if (!roomDoc || !identity) return

      // Find the participant to get their public key
      const participant = participants.find((p) => p.peerId === peerId)
      if (!participant) return

      // Check if we are the organizer (have the organizer's keys)
      const organizerPubKey = getOrganizerPublicKey(roomDoc)
      const isOrganizer = organizerPubKey && isOrganizerByKey(identity.publicKeyBase64, organizerPubKey)

      if (!isOrganizer) {
        console.warn('[useRoom] Only organizer can assign roles cryptographically')
        // Still update locally for backward compatibility
        addParticipant(roomDoc, { ...participant, role })
        return
      }

      // For participants with a public key, create a signed role assignment
      // Note: We need the participant's public key from their messages
      // For now, we use the peerId as a fallback identifier
      const participantPubKey = getRoleAssignment(roomDoc, peerId)?.assigneePublicKey || peerId

      try {
        // Create a cryptographically signed role assignment
        const roleAssignment = await createRoleAssignment(
          participantPubKey,
          role,
          roomId,
          identity.privateKey,
          identity.publicKey
        )

        // Store the role assignment in CRDT
        setRoleAssignment(roomDoc, roleAssignment)
        console.log('[useRoom] Created signed role assignment for:', peerId, role)
      } catch (err) {
        console.error('[useRoom] Failed to create role assignment:', err)
      }

      // Also update the participant record
      addParticipant(roomDoc, { ...participant, role })
    },
    [roomDoc, identity, participants, roomId]
  )

  const updateMyGeofenceStatus = useCallback(
    (isInside: boolean) => {
      if (!roomDoc || !myPeerId) return
      updateParticipant(roomDoc, myPeerId, { isInsideGeofence: isInside })
    },
    [roomDoc, myPeerId]
  )

  const kickParticipant = useCallback(
    (peerId: string) => {
      if (!roomDoc) return
      removeParticipant(roomDoc, peerId)
    },
    [roomDoc]
  )

  const endRoom = useCallback(() => {
    if (!roomDoc) return

    // Add system message about room ending
    addCRDTMessage(roomDoc, {
      id: crypto.randomUUID(),
      type: 'system',
      senderId: 'system',
      senderName: 'System',
      senderRole: 'participant',
      timestamp: Date.now(),
      content: `Room ended by ${myDisplayName}`,
    })

    // Mark room as ended in meta
    updateRoomMeta(roomDoc, { isPaused: true })
    setIsEnded(true)
  }, [roomDoc, myDisplayName])

  const leave = useCallback(async () => {
    if (roomDoc) {
      removeParticipant(roomDoc, myPeerId)
      await destroyRoomDocument(roomId)
    }
    setIsConnected(false)
  }, [roomDoc, roomId, myPeerId])

  const currentSpeaker = useMemo(
    () => speakingQueue[0] ?? null,
    [speakingQueue]
  )

  return {
    isConnected,
    isEnded,
    roomMeta,
    participants,
    messages,
    activePOIs,
    speakingQueue,
    currentSpeaker,
    roomDoc, // Exposed for Bluetooth bridge
    sendTextMessage,
    sendVoiceMessage,
    sendPOI,
    sendVehicleSighting,
    clearPOI,
    joinQueue,
    leaveQueue,
    updateMeta,
    updateParticipantRole,
    updateMyGeofenceStatus,
    kickParticipant,
    endRoom,
    leave,
  }
}
